// src/scene/CameraController.tsx
/**
 * Spring-Damped Camera Controller
 *
 * Smooth, cinematic camera transitions between stages.
 * Uses @react-spring/three for physically-based animation.
 * No jitter, stable lookAt.
 */

import { useRef, useEffect } from 'react'
import { useFrame, useThree } from '@react-three/fiber'
import { Vector3, PerspectiveCamera } from 'three'
import { useShallow } from 'zustand/react/shallow'
import { useControls, folder } from 'leva'
import { useStageStore } from './useStageStore'
import { useDesktopLayout, DESKTOP_CAMERA } from './useResponsiveLayout'

// ============================================================================
// CAMERA CONTROLLER
// ============================================================================

export function CameraController() {
  const { camera } = useThree()
  const updateCameraPosition = useStageStore((state) => state.updateCameraPosition)
  const targetVec = useRef(new Vector3())
  const positionVec = useRef(new Vector3())

  // Leva controls for camera - defaults to DESKTOP_CAMERA values (primary use case)
  const {
    posX, posY, posZ,
    targetX, targetY, targetZ,
    fov, near, far,
  } = useControls('Camera', {
    Position: folder({
      posX: { value: DESKTOP_CAMERA.position[0], min: -2, max: 8, step: 0.001 },
      posY: { value: DESKTOP_CAMERA.position[1], min: 0, max: 2, step: 0.001 },
      posZ: { value: DESKTOP_CAMERA.position[2], min: 2, max: 8, step: 0.001 },
    }),
    Target: folder({
      targetX: { value: DESKTOP_CAMERA.target[0], min: -2, max: 2, step: 0.001 },
      targetY: { value: DESKTOP_CAMERA.target[1], min: 0, max: 2, step: 0.001 },
      targetZ: { value: DESKTOP_CAMERA.target[2], min: 0, max: 4, step: 0.001 },
    }),
    Lens: folder({
      fov: { value: DESKTOP_CAMERA.fov, min: 10, max: 120, step: 0.5 },
      near: { value: DESKTOP_CAMERA.near, min: 0.01, max: 10, step: 0.001 },
      far: { value: DESKTOP_CAMERA.far, min: 10, max: 10000, step: 1 },
    }),
  })

  // Smooth interpolation toward target camera pose (frame-rate independent)
  useFrame((_state, delta) => {
    // Use Leva controls in both desktop and mobile modes
    const targetPos = [posX, posY, posZ] as [number, number, number]
    const targetLook = [targetX, targetY, targetZ] as [number, number, number]
    const targetFov = fov

    // Frame-rate independent lerp: consistent speed at 30fps, 60fps, or 120fps
    const t = 1 - Math.exp(-4 * delta)

    // Smooth lerp for position
    positionVec.current.set(targetPos[0], targetPos[1], targetPos[2])
    camera.position.lerp(positionVec.current, t)

    // Smooth lerp for target
    targetVec.current.set(targetLook[0], targetLook[1], targetLook[2])
    camera.lookAt(targetVec.current)

    // Apply lens settings (PerspectiveCamera)
    if ('fov' in camera) {
      const perspCam = camera as PerspectiveCamera
      // Smooth FOV transition
      perspCam.fov += (targetFov - perspCam.fov) * t
      perspCam.near = near
      perspCam.far = far
      perspCam.updateProjectionMatrix()
    }

    updateCameraPosition(camera.position, targetVec.current)
  })

  return null
}

// ============================================================================
// INPUT HANDLER (scroll, keyboard, touch)
// ============================================================================

export function CameraInputHandler() {
  const { handleScroll, handleSwipe, nextStage, prevStage } = useStageStore(
    useShallow((state) => ({
      handleScroll: state.handleScroll,
      handleSwipe: state.handleSwipe,
      nextStage: state.nextStage,
      prevStage: state.prevStage,
    }))
  )
  const { isDesktopLayout } = useDesktopLayout()

  const touchStart = useRef<{ x: number; y: number } | null>(null)

  useEffect(() => {
    // In desktop layout, all panels visible - no stage navigation needed
    if (isDesktopLayout) return

    // Scroll handler (mobile only)
    const onWheel = (e: WheelEvent) => {
      e.preventDefault()
      handleScroll(e.deltaY)
    }

    // Keyboard handler (mobile only)
    const onKeyDown = (e: KeyboardEvent) => {
      switch (e.key) {
        case 'ArrowDown':
        case 'ArrowRight':
          nextStage()
          break
        case 'ArrowUp':
        case 'ArrowLeft':
          prevStage()
          break
      }
    }

    // Touch handlers (mobile only)
    const onTouchStart = (e: TouchEvent) => {
      const touch = e.touches[0]
      touchStart.current = { x: touch.clientX, y: touch.clientY }
    }

    const onTouchEnd = (e: TouchEvent) => {
      if (!touchStart.current) return

      const touch = e.changedTouches[0]
      const deltaX = touch.clientX - touchStart.current.x
      const deltaY = touch.clientY - touchStart.current.y

      handleSwipe(deltaX, deltaY)
      touchStart.current = null
    }

    // Add listeners
    window.addEventListener('wheel', onWheel, { passive: false })
    window.addEventListener('keydown', onKeyDown)
    window.addEventListener('touchstart', onTouchStart, { passive: true })
    window.addEventListener('touchend', onTouchEnd, { passive: true })

    return () => {
      window.removeEventListener('wheel', onWheel)
      window.removeEventListener('keydown', onKeyDown)
      window.removeEventListener('touchstart', onTouchStart)
      window.removeEventListener('touchend', onTouchEnd)
    }
  }, [isDesktopLayout, handleScroll, handleSwipe, nextStage, prevStage])

  return null
}

// ============================================================================
// COMBINED CAMERA SYSTEM
// ============================================================================

export function CameraSystem() {
  return (
    <>
      <CameraController />
      <CameraInputHandler />
    </>
  )
}

export default CameraController
